import React, { useCallback, useState } from 'react';
import { ISubscription } from '../../../redux/subscriptions/types';
import { JSX } from '@ionic/core/components';
import { FullWidthModal } from '../SubscriptionControl.styled';
import { Container } from '@mindly/ui-components';
import { IonContent, IonIcon } from '@ionic/react';
import { closeCircleOutline, playCircleOutline } from 'ionicons/icons';
import { useDispatch, useSelector } from 'react-redux';
import { patchSubscription } from '../../../api/subscription';
import { fetchSubscriptions } from '../../../redux/subscriptions/thunk';
import { userSelector } from '../../../redux/user/selector';
import useAgoraRtm from '../../../context/AgoraContext/AgoraContext';
import { SystemMessagesType, UserType } from '../../types';
import { selectRoomDataBySenderId } from '../../../redux/chatRooms/selector';
import { FormattedMessage, useIntl } from 'react-intl';
import useLangs from '../../../context/LangsContext';
import { formatSystemMessage } from '../../../context/AgoraContext/formatSystemMessage';
import EmailSystem from '../../../api/emails';
import PageHeader from '../../../components/PageHeader/PageHeader';
import { CancelSubscriptionModalWrapper } from '../CancelSubscriptionModal/CancelSubscriptionModal.style';
import FirstStep from './Steps/FirstStep';
import LoaderStep from './Steps/LoaderStep';
import FinishStep from './Steps/FinishStep';
import HelpButton from '../../../components/HelpButton';
import { SUBSCRIPTION_PAUSE } from '../../../intercom-articles';
import useToast from '../../../context/ToastContext/ToastContext';
import { useAnalytics } from 'src/shared';
type UnpauseModalProps = {
  onDidDismiss: () => void;
  subscription?: ISubscription | null;
} & JSX.IonModal;

const UnpauseModal: React.FC<UnpauseModalProps> = ({ subscription, ...props }) => {
  const intl = useIntl();
  const { locale } = useLangs();

  const dispatch = useDispatch();
  const { trackPredefinedEvent } = useAnalytics();

  const { showToast } = useToast();
  const [patchedSubscription, setPatchedSubscription] = useState<null | ISubscription>(null);
  const [step, setStep] = useState(1);

  const user = useSelector(userSelector);
  const roomData = useSelector(selectRoomDataBySenderId(subscription?.therapist_id ?? ''));

  const { sendSystemMessage } = useAgoraRtm();

  const unpauseSubscriptionHandler = useCallback(async () => {
    try {
      if (!subscription?.id)
        throw new Error(
          intl.formatMessage({
            id: 'subscription.error.data_not_loaded_yet',
            defaultMessage: 'Схоже дані не завантажились.',
          })
        );

      setStep(2);
      const result = await patchSubscription(
        {
          pause_collection: false,
        },
        user?.id,
        subscription?.id
      );
      setPatchedSubscription(result.subscription_data);

      const localSubscriptions = await dispatch(fetchSubscriptions(user?.id)).unwrap();
      const localSubscription = localSubscriptions.find((el) => el.id === subscription.id);
      if (!localSubscription) throw new Error('no_such_subscription');
      sendSystemMessage({
        user: user as Omit<UserType, 'id'> & { id: string },
        specialistId: localSubscription?.therapist_id,
        message: formatSystemMessage(
          SystemMessagesType.subscription_un_paused,
          {
            date: new Date(+localSubscription.period_end * 1000).toLocaleString(locale, {
              day: 'numeric',
              month: 'long',
              year: 'numeric',
            }),
            therapistName: `${subscription.therapist.first_name} ${subscription.therapist.last_name[0]}`,
          },
          intl
        ),
        notifyMessage: intl.formatMessage({
          id: 'subscription.notify_message.un_pause',
          defaultMessage: 'Активував(ла) підписку',
        }),
        roomData,
        locale,
      });
      EmailSystem.sendSubscription('activated', subscription.id);

      trackPredefinedEvent('SUBSCRIPTION_RESTART_COMPLETED', {
        subscription_id: subscription.id,
        specialist_id: subscription.therapist_id,
      });

      setStep(3);
    } catch (error) {
      console.error(error);
      showToast({ error });
      setStep(1);
    }
  }, [subscription, user]);

  const handleCloseModal = useCallback(() => {
    props.onDidDismiss();
    setStep(1);
  }, [props.onDidDismiss]);

  return (
    <FullWidthModal
      {...props}
      onIonModalDidDismiss={() => {
        setStep(1);
      }}
    >
      {step !== 2 && (
        <PageHeader backChevron={step === 1} backChevronAction={handleCloseModal} showAvatar={step !== 3}>
          <IonIcon style={{ fontSize: '27px' }} icon={closeCircleOutline} onClick={handleCloseModal} />
        </PageHeader>
      )}
      <IonContent>
        <Container
          style={{
            paddingTop: 0,
            marginTop: '-15px',
            justifyContent: 'center',
          }}
        >
          <CancelSubscriptionModalWrapper>
            {step === 1 && <IonIcon icon={playCircleOutline} className={'play-icon'} />}
            {(() => {
              switch (step) {
                case 1:
                  return <FirstStep handleNextStep={unpauseSubscriptionHandler} />;
                case 2:
                  return <LoaderStep />;
                case 3:
                  return <FinishStep handleClose={handleCloseModal} subscription={patchedSubscription} />;
              }
            })()}
          </CancelSubscriptionModalWrapper>
          {step === 1 && (
            <HelpButton articleId={SUBSCRIPTION_PAUSE} style={{ position: 'fixed', bottom: '50px' }}>
              <FormattedMessage id={'subscription.pause.help.btn'} defaultMessage={'Як це працює?'} />
            </HelpButton>
          )}
        </Container>
      </IonContent>
    </FullWidthModal>
  );
};

export default UnpauseModal;
